/**
 * Keeps product-card image rotation cheap.
 *
 * Horizon's <slideshow-component> autoplay runs a setInterval regardless of whether
 * the card is on screen, so a long collection page would keep dozens of timers (and
 * their scroll animations) alive at once. This suspends every card slideshow that
 * leaves the viewport and resumes it when it comes back.
 *
 * suspend()/resume() are used rather than pause()/play() because pause() sets the
 * `paused` flag, which resume() then refuses to clear.
 */
(() => {
  const SELECTOR = 'slideshow-component[data-card-autoplay="true"]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const slideshow = /** @type {any} */ (entry.target);
        if (typeof slideshow.suspend !== 'function') continue;

        if (entry.isIntersecting && !reduceMotion.matches) {
          slideshow.resume?.();
        } else {
          slideshow.suspend();
        }
      }
    },
    { rootMargin: '200px 0px' }
  );

  const observed = new WeakSet();

  const scan = (root = document) => {
    for (const el of root.querySelectorAll?.(SELECTOR) ?? []) {
      if (observed.has(el)) continue;
      observed.add(el);
      observer.observe(el);
    }
  };

  const start = () => {
    scan();

    // Collection filtering, pagination and section rendering all swap card markup in.
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const el = /** @type {Element} */ (node);
          if (el.matches?.(SELECTOR)) {
            if (!observed.has(el)) {
              observed.add(el);
              observer.observe(el);
            }
          }
          scan(el);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    reduceMotion.addEventListener('change', () => {
      if (!reduceMotion.matches) return;
      for (const el of document.querySelectorAll(SELECTOR)) {
        /** @type {any} */ (el).suspend?.();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
